import moment from 'moment';
import {useAggSalesRISAQuery} from "./sales";
import React, {useMemo} from "react";
import {hasColumn, processBUDeptDict} from './utils';
import {rollupDataBy} from '../../../../common/utils';
import {useSelectorValue} from "../../common/providers/selectorProvider";
import {useDashboardMetaContext} from "../../common/providers/DashboardMetaProvider";
import {FIELD_BU, FIELD_DEPARTMENT} from "../dashboardDefinition";
import {PERIOD_FORMAT} from "./constants";


export const useMaxRecordDateMoment = () => {
    const { asArcDateRange: asDateRange, isLoading } = usePeriodValues();

    if (!asDateRange || isLoading) {
        return {
            data: null,
            isLoading: isLoading
        }
    }
    return {
        data: moment(asDateRange.to, "YYYY-MM-DD"),
        isLoading: isLoading
    }
}

export const useMaxPeriodDateMoment = () => {
    const { asPeriodDateRange, isLoading } = usePeriodValues();
    if (!asPeriodDateRange || isLoading) {
        return {
            data: null,
            isLoading: isLoading
        }
    }
    return {
        data: moment(asPeriodDateRange.to, "YYYY-MM-DD"),
        isLoading: isLoading
    }
}


export const useMaxArcDate = () => {
    const { asArcDateRange, isLoading } = usePeriodValues();

    if (!asArcDateRange || isLoading) {
        return {
            data: null,
            isLoading: isLoading
        }
    }
    return {
        data: moment(asArcDateRange.to, "YYYY-MM-DD"),
        isLoading: isLoading
    }
}


export const usePeriodValues = () => {
    const { data, isLoading, ...rest } = useAggSalesRISAQuery({
        aggregates: [
            'arc_date_max', 'arc_date_min',
            'period_max', 'period_min',
        ],
    });



    const asArcDateRange = React.useMemo(() => {
        if (!data || isLoading) return null;

        return {
            from: moment(data[0][`arc_date_min`], "YYYYMMDD"),
            to: moment(data[0][`arc_date_max`], "YYYYMMDD"),
        }
    }, [data, isLoading]);

    const asPeriodDateRange = React.useMemo(() => {
        if (!data || isLoading) return null;

        return {
            from: moment(data[0][`period_min`], PERIOD_FORMAT),
            to: moment(data[0][`period_max`], PERIOD_FORMAT),
        }
    }, [data, isLoading]);

    return {
        ...rest,
        data,
        asArcDateRange,
        asPeriodDateRange,
        isLoading
    }
}

export const useBUDeptGroupings = () => {
    const salesFiltersFromDefinition = useDashboardMetaContext('sales_filters') || {};
    const orgEnvFilter = useOrgEnvFilterFromSourceSelector();

    const params = {
        ...salesFiltersFromDefinition,
        ...orgEnvFilter,
        columns: [FIELD_BU, FIELD_DEPARTMENT],
        group_by: [FIELD_BU, FIELD_DEPARTMENT],
        order_by: [FIELD_BU, FIELD_DEPARTMENT].join(','),

        limit: 150,
    }
    const hasSubDept = true

    // const { data: salesDepts } = useFactTSSJoinQuery({ ...params }, { enabled: true }, hasSubDept);
    const { data } = useAggSalesRISAQuery({ ...params }, { enabled: true }, true);


    const buDeptMaps = useMemo(() => {
        if (!data || data?.length < 1) {
            return {
                buToDeptDict: {},
                deptList: []
            }
        }

        const { bu_to_dept, dept_list } = processBUDeptDict(data);

        return {
            buToDeptDict: bu_to_dept,
            deptList: dept_list,
        }
    }, [data]);

    const buData = rollupDataBy(data, [FIELD_BU], []);
    const departmentData = rollupDataBy(data, [FIELD_DEPARTMENT], []);

    return {
        data,
        buToDeptDict: buDeptMaps['buToDeptDict'],
        deptList: buDeptMaps['deptList'],
        buData,
        departmentData
        // ...rest
    }
}


export const createParentMapping = (data, parents = [], currentMapping = {}) => {
    return data.reduce((acc, row) => {
        const value = row['pk'];
        const pkKey = row['pkKey'];
        acc[`${pkKey}|${value}`] = parents;
        if (row['subRows'] && row['subRows'].length > 0) {
            const newParents = [...parents, { pk: value, pkKey: pkKey }];
            createParentMapping(row['subRows'], newParents, acc);
        }
        return acc
    }, currentMapping)
}

export const createParentMappingV2 = (data, parents = [], currentMapping = {}) => {
    if (!data || data.length < 1) {
        return [];
    }

    return data.reduce((acc, row) => {
        const bu_grp = row['bu_grp'];
        const bu_desc = row[FIELD_BU];
        const dept_desc_standardized = row[FIELD_DEPARTMENT];

        // Helper function to add unique parent objects
        const addUniqueParent = (key, parent) => {
            if (!acc[key]) {
                acc[key] = [];
            }
            if (!acc[key].some(item => item.pk === parent.pk && item.pkKey === parent.pkKey)) {
                acc[key].push(parent);
            }
        };

        addUniqueParent(`bu_desc|${bu_desc}`, { pk: bu_grp, pkKey: 'bu_grp' });
        addUniqueParent(`dept_desc_standardized|${dept_desc_standardized}`, { pk: bu_grp, pkKey: 'bu_grp' });
        addUniqueParent(`dept_desc_standardized|${dept_desc_standardized}`, { pk: bu_desc, pkKey: FIELD_BU });

        // Ensure bu_grp key exists in the accumulator
        if (!acc[`bu_grp|${bu_grp}`]) {
            acc[`bu_grp|${bu_grp}`] = [];
        }

        return acc;
    }, {});
}


export const useStoreNames = () => {
    const branchNameColumn = useDashboardMetaContext('branch_name_column');
    const salesFiltersFromDefinition = useDashboardMetaContext('sales_filters') || {};
    const hasSubDept = hasColumn(salesFiltersFromDefinition, 'subdept_desc_standardized') ||
        hasColumn(salesFiltersFromDefinition, 'dept_desc_standardized') ||
        hasColumn(salesFiltersFromDefinition, 'bu_desc') ||
        hasColumn(salesFiltersFromDefinition, 'bu_grp');
    const orgEnvFilter = useOrgEnvFilterFromSourceSelector();

    const { data, ...rest } = useAggSalesRISAQuery({
        ...salesFiltersFromDefinition,
        ...orgEnvFilter,
        group_by: [branchNameColumn],
        limit: 1000
    }, {}, hasSubDept, true)

    return {
        data: data?.map(row => row[branchNameColumn]),
        ...rest
    }
}


export const useOrgEnvFilterFromSourceSelector = () => {
    const raSourceStandAlone = useSelectorValue('ra_source_stand_alone', true);
    const raSourceDSP = useSelectorValue('ra_source_dsp', true);
    const raSourceMarkets = useSelectorValue('ra_source_markets', true);

    if ((raSourceStandAlone && raSourceDSP && raSourceMarkets) || (!raSourceStandAlone && !raSourceDSP && !raSourceMarkets)) {
        return {}
    }

    const dspOrgEnvCode = ['DSP'];
    const marketsOrgEnvCode = ['RA_MKT', 'ESTORE'];
    const raOrgEnvCodes = [
        'FAP',
        'CBP',
		'SCP',
		'SAP',
		'WAP',
		'HWP',
		'SSP',
		'ECP',
		'TWP',
		'BSP',
		'NCP',
		'SHP',
		'MSP',
		'KSP',
		'MBP',
		'DYP',
		'HWP',
		'SMP',
		'SLP',
		'CMP',
		'UQO',
		'WZP'
    ]

    const orgEnvCode = [
        ...(raSourceStandAlone ? raOrgEnvCodes : []),
        ...(raSourceDSP ? dspOrgEnvCode : []),
        ...(raSourceMarkets ? marketsOrgEnvCode : [])
    ];

    return {
        org_env_code: orgEnvCode,
        org_code: orgEnvCode
    }
}